import { Link } from 'react-router-dom';

import { format, differenceInCalendarDays } from 'date-fns';
import css from './CalendarTable.module.css';

function CalendarTable({ courses, displayPeriod, cellWidth, CATEGORY_WIDTH, tooltip, setTooltip }) {
  const isToday = (date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getItemOffsetAndSpan = (item) => {
    const offset = differenceInCalendarDays(new Date(item.start), new Date(displayPeriod.startDate));
    const span = differenceInCalendarDays(new Date(item.end), new Date(item.start)) + 1;
    return { offset, span };
  };

  const assignTracks = (items) => {
    const tracks = [];
    const sortedItems = [...items].sort((a, b) => new Date(a.start) - new Date(b.start));
    const placedItems = [];

    sortedItems.forEach((item) => {
      const itemStart = new Date(item.start);
      const itemEnd = new Date(item.end);
      let placed = false;

      for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
        const track = tracks[trackIndex];
        const overlap = track.some(
          (otherItem) => new Date(otherItem.start) <= itemEnd && new Date(otherItem.end) >= itemStart
        );

        if (!overlap) {
          track.push(item);
          placedItems.push({ ...item, trackIndex });
          placed = true;
          break;
        }
      }

      if (!placed) {
        tracks.push([item]);
        placedItems.push({ ...item, trackIndex: tracks.length - 1 });
      }
    });

    return { placedItems, trackCount: tracks.length };
  };

  const groupByMonth = () => {
    const groups = [];
    if (!displayPeriod.dates || displayPeriod.dates.length === 0) return groups;

    let currentMonth = format(displayPeriod.dates[0], 'MMM yyyy');
    let count = 0;

    displayPeriod.dates.forEach((date) => {
      const label = format(date, 'MMM yyyy');
      if (label !== currentMonth) {
        groups.push({ label: currentMonth, span: count });
        currentMonth = label;
        count = 1;
      } else {
        count++;
      }
    });

    if (count > 0) {
      groups.push({ label: currentMonth, span: count });
    }

    return groups;
  };

  const handleMouseEnter = (e, item) => {
    const tooltipContent = `
      <strong>(${item.courseNo}) ${item.title}</strong><br/>
      ${item.start} → ${item.end}<br/>
      Category: ${item.category}`;
    setTooltip({ visible: true, content: tooltipContent, x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    setTooltip((prev) => ({ ...prev, x: e.clientX, y: e.clientY }));
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, content: '', x: 0, y: 0 });
  };

  const monthGroups = groupByMonth();

  return (
    <div className={css.table}>
      <div className={css.monthRow} style={{ gridTemplateColumns: `200px repeat(${displayPeriod.totalDays}, ${cellWidth}px)` }}>
        <div></div>
        {monthGroups.map((group, idx) => (
          <div key={idx} className={css.monthCell} style={{ gridColumn: `span ${group.span}` }}>
            {group.label}
          </div>
        ))}
      </div>

      <div className={`${css.row} ${css.headerRow}`} style={{ gridTemplateColumns: `200px repeat(${displayPeriod.totalDays}, ${cellWidth}px)` }}>
        <div className={css.categoryHeader}>Category</div>
        {displayPeriod.dates.map((date, index) => (
          <div key={index} className={`${css.dateCell} ${isToday(date) ? css.todayCell : ''}`} >{format(date, 'd')}</div>
        ))}
      </div>

      {Array.from(new Set(courses.map((i) => i.category))).map((category, rowIndex) => {
        const rowItems = courses.filter((item) => item.category === category);
        const { placedItems, trackCount } = assignTracks(rowItems);
        const isEven = rowIndex % 2 === 0;

        return (
          <div
            key={rowIndex}
            className={`${css.rowWrapper} ${isEven ? css.bandedRowEven : css.bandedRowOdd}`}
            style={{ height: `${trackCount * 48}px` }}
          >
            <div className={css.row} style={{ gridTemplateColumns: `200px repeat(${displayPeriod.totalDays}, ${cellWidth}px)` }}>
              <div className={css.categoryCell}>{category}</div>
              {displayPeriod.dates.map((_, index) => (
                <div key={index} className={css.emptyCell}></div>
              ))}
            </div>

            {placedItems.map((item) => {
              const { offset, span } = getItemOffsetAndSpan(item);
              return (
                <Link to={`/course/details/${item.id}`} className={css.itemLink}>
                <div
                  key={item.id}
                  className={css.itemBar}
                  style={{
                    left: `calc(${CATEGORY_WIDTH}px + ${offset * cellWidth}px + 2px)`,
                    width: `calc(${span * cellWidth}px - 22px)`,
                    backgroundColor: item.color,
                    top: `${item.trackIndex * 48 + 8}px`,
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, item)}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >

                  ({item.courseNo}) {item.title.slice(0, Math.floor(cellWidth * span / 4)) /* Adjusted to fit within the cell width */}
                </div>
                </Link>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default CalendarTable;
